import { isInIframe } from './iframeUtil'

const getHostFromSearchParams = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('host')
}

/**
 * True when the SPA was opened with `?llm_wordcloud=true`.
 *
 * monitor-app's `/cb/nb/` shell forwards that query param into the SPA iframe
 * src (e.g. `…/cb/web/dashboard/808913?llm_wordcloud=true`), so the flag is
 * readable from this frame's own `window.location.search`. It is NOT visible
 * to monitor-app's Referer-fallback (the runSearches Referer is the CDN origin,
 * not the dashboard URL), which is why the SPA has to surface it explicitly.
 *
 * Used as a manual-testing escape hatch alongside the LaunchDarkly feature flag.
 */
export const isLlmWordCloudUrlFlagSet = (): boolean =>
  new URLSearchParams(window.location.search).get('llm_wordcloud') === 'true'

const isLocalDevelopment = (): boolean =>
  process.env.NODE_ENV === 'development' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

/**
 * Resolve the origin that serves the `/cb/api/2/` backend for the current page.
 *
 * The SPA bundle ships to TWO origins: the monitor-app WAR on the API host
 * (e.g. https://monitor.quid.com) and a static CDN bucket (`REACT_APP_CDN_URL`,
 * e.g. https://monitor-web.quid.com) that serves HTML/JS but does NOT proxy
 * `/cb/api/2/*`. So a page's own origin is the API host only when the WAR served it.
 *
 * Resolution order (first match wins):
 *   1. `?host=` query param — explicit override for cross-origin embeds / manual testing.
 *   2. Local development — API proxied to the dev backend on :3000.
 *   3. Embedded dashboard (in monitor-app's `/cb/nb/` iframe) — the bundle is CDN-served,
 *      so the API host is the embedding parent, carried by `document.referrer`.
 *   4. Standalone top-level page (live report, unsubscribe) — the page's own origin, unless
 *      it was served by the CDN bucket, in which case the build-time `REACT_APP_PROXY`.
 */
export const getBaseUrl = (): string => {
  // 1. Explicit override — cross-origin embeds / manual testing (?host=acme.quid.com).
  const hostParam = getHostFromSearchParams()
  if (hostParam) {
    return `https://${hostParam}`
  }

  // 2. Local development: API calls are proxied to the dev backend by the CRA dev
  //    server / Express proxy listening on :3000 (package.json "proxy" / server.js).
  if (isLocalDevelopment()) {
    return 'http://localhost:3000'
  }

  // 3. Embedded in monitor-app's /cb/nb/ shell (dashboards): the bundle is CDN-served, so
  //    this frame's own origin is not the API host — the embedding monitor-app parent is,
  //    carried by document.referrer. Keeps the existing per-cluster dynamic resolution for
  //    dev/staging/prod dashboards unchanged.
  if (isInIframe() && document.referrer) {
    return new URL(document.referrer).origin
  }

  // 4. Standalone top-level page (a live report opened from an email link, a new tab, a
  //    bookmark; or an unsubscribe page). When the WAR served the page, its own origin IS
  //    the API host. The sole exception is the static CDN bucket, which can also serve the
  //    HTML but has no API — there we use the build-time configured API host
  //    (REACT_APP_PROXY). We deliberately ignore document.referrer (an external referrer,
  //    e.g. a webmail client, would be the wrong host) and never fall back to localhost —
  //    the bug this replaces, where a directly-opened live report hit http://localhost:3000.
  const { origin } = window.location
  if (process.env.REACT_APP_CDN_URL && origin === process.env.REACT_APP_CDN_URL) {
    return process.env.REACT_APP_PROXY || origin
  }
  return origin
}
